import React from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Calculator,
  ArrowRight,
  CheckCircle,
  Shield,
  Phone,
  TreePine,
  Palette,
  Leaf,
  Award,
  Crown,
  Sparkles
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BasswoodPage = () => {
  const features = [
    { text: 'FSC Certified Basswood Hardwood', icon: Leaf },
    { text: 'Premium Basswood Timber Construction', icon: TreePine },
    { text: 'Superior Quality & Craftsmanship', icon: Crown },
    { text: 'Finest Natural Wood Finish', icon: Sparkles },
    { text: 'Exceptional Durability & Strength', icon: Shield },
    { text: 'Smooth Precision Operation', icon: Award },
    { text: 'Luxury Appearance & Feel', icon: Crown },
    { text: 'Available in 30+ Colours', icon: Palette },
    { text: '6 Natural Wood Stains', icon: Palette },
    { text: 'Custom Colours Available', icon: Palette },
    { text: '10 Year Structural Warranty', icon: Shield },
    { text: 'Hand-Finished to Perfection', icon: Sparkles }
  ];

  const bestFor = [
    'Master bedrooms',
    'Formal dining rooms',
    'Executive offices',
    'Luxury living spaces',
    'Period properties',
    'High-end renovations'
  ];

  const faqs = [
    {
      question: 'What makes Basswood shutters premium?',
      answer: 'Basswood is one of the finest furniture-grade hardwoods available. Its natural properties include exceptional dimensional stability, resistance to warping, and a beautiful fine grain that takes finishes superbly. Our Basswood shutters are crafted with the highest attention to detail, resulting in a truly luxurious product.'
    },
    {
      question: 'How do Basswood shutters compare to Paulownia?',
      answer: 'While Paulownia offers excellent value, Basswood represents our premium tier with superior wood density, finer grain patterns, and enhanced durability. Basswood shutters have a more substantial feel and are ideal for discerning homeowners who want the absolute best in natural wood shutters.'
    },
    {
      question: 'Are Basswood shutters worth the investment?',
      answer: 'Absolutely. Basswood shutters are a long-term investment in your home. They add significant value to your property, offer unmatched natural beauty, and come with our 10-year structural warranty. For luxury homes and quality-conscious homeowners, Basswood is the ultimate choice.'
    },
    {
      question: 'What finishes are available for Basswood shutters?',
      answer: 'Basswood can be finished in over 30 painted colours or 6 natural wood stains that showcase the beautiful grain. We also offer custom colour matching. The natural beauty of Basswood is particularly stunning in stained finishes that allow the wood grain to shine through.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20" style={{background: 'linear-gradient(to bottom right, #fef8f0, #fdf4e8)'}}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-100 mb-6">
                <Crown className="h-5 w-5 text-amber-600" />
                <Badge className="bg-amber-600 text-white">Premium Range</Badge>
              </div>
              
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Basswood Hardwood Shutters
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Our premium range showcases the finest FSC certified Basswood hardwood timber shutters. 
                Exceptional quality and superior craftsmanship for the most discerning homeowners seeking luxury.
              </p>
              
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Shield className="h-5 w-5 text-amber-600" />
                  <span className="font-semibold text-gray-900">10 Year Warranty</span>
                </div>
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm">
                  <Crown className="h-5 w-5 text-amber-600" />
                  <span className="font-semibold text-gray-900">Premium Quality</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/calculator">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-4 text-lg">
                    <Calculator className="mr-2 h-5 w-5" />
                    Use Our Online Estimator
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50 px-8 py-4 text-lg">
                    <Phone className="mr-2 h-5 w-5" />
                    Book Free Survey
                  </Button>
                </Link>
              </div>
            </div>
            
            <div className="relative">
              <div className="absolute -top-4 -right-4 bg-amber-600 text-white px-4 py-2 rounded-full font-semibold shadow-lg z-10 flex items-center gap-2">
                <Crown className="h-4 w-4" />
                Premium
              </div>
              <img 
                src="https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/leqxhvi8_01.%20Taupe%20Bay%20Shutters.jpg"
                alt="Basswood Hardwood Shutters"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Premium Banner */}
      <section className="py-8 bg-amber-600">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            <div className="flex flex-col items-center gap-2">
              <Crown className="h-8 w-8" />
              <span className="font-semibold">Luxury Quality</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Leaf className="h-8 w-8" />
              <span className="font-semibold">FSC Certified</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Sparkles className="h-8 w-8" />
              <span className="font-semibold">Hand-Finished</span>
            </div>
            <div className="flex flex-col items-center gap-2">
              <Shield className="h-8 w-8" />
              <span className="font-semibold">10 Year Warranty</span>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Uncompromising Quality</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Every Basswood shutter is crafted to the highest standards, delivering exceptional beauty and performance.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="p-2 rounded-lg bg-amber-50">
                      <feature.icon className="h-6 w-6 text-amber-600" />
                    </div>
                    <span className="text-gray-700 font-medium pt-2">{feature.text}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Best For Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">For the Finest Spaces</h2>
              <p className="text-lg text-gray-600 mb-8">
                Basswood Hardwood Shutters are the ultimate choice for luxury interiors. 
                Their exceptional quality and timeless elegance make them perfect for homes where only the best will do.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {bestFor.map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm">
                    <Crown className="h-5 w-5 text-amber-600" />
                    <span className="text-gray-700 font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_window-quotes-1/artifacts/leqxhvi8_01.%20Taupe%20Bay%20Shutters.jpg"
                alt="Basswood Shutters in Luxury Interior"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Basswood Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">The Basswood Difference</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Discover why Basswood is the choice of luxury homeowners.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <TreePine className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Superior Wood Quality</h3>
                <p className="text-gray-600">Basswood's natural properties provide exceptional stability, durability, and a fine grain finish.</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Sparkles className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Expert Craftsmanship</h3>
                <p className="text-gray-600">Each shutter is hand-finished by skilled craftsmen to ensure perfection in every detail.</p>
              </CardContent>
            </Card>
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8 text-center">
                <Award className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Lasting Value</h3>
                <p className="text-gray-600">A premium investment that adds beauty and value to your home for decades to come.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600">
              Common questions about our Basswood Hardwood Shutters
            </p>
          </div>
          
          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">{faq.question}</h3>
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white" style={{backgroundColor: '#8CB89F'}}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Experience Premium Luxury</h2>
          <p className="text-xl mb-8 opacity-90">
            Get an instant estimate for Basswood Hardwood Shutters using our online calculator.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/calculator">
              <Button size="lg" className="bg-white hover:bg-gray-100 px-8 py-4 text-lg" style={{color: '#8CB89F'}}>
                <Calculator className="mr-2 h-5 w-5" />
                Use Our Online Estimator
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/materials">
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white text-white hover:bg-white/10 px-8 py-4 text-lg"
              >
                Compare All Ranges
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BasswoodPage;
